import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { markets, marketList, cartUrl } from '../data/markets';
import { Market, MarketCode } from '../types/market';
import { fetchShopifyPrice } from '../utils/shopifyPrices';
import { formatLocal } from '../utils/money';
interface MarketContextValue {
  market: Market;
  markets: Market[];
  setMarketCode: (code: MarketCode) => void;
  /** Cart permalink pinned to the active Shopify market. */
  cartHref: string;
}
const MarketContext = createContext<MarketContextValue | null>(null);
const TIMEZONE_MARKET: Record<string, MarketCode> = {
  'America/Bogota': 'CO',
  'America/Mexico_City': 'MX',
  'America/Monterrey': 'MX',
  'America/Tijuana': 'MX',
  'America/Cancun': 'MX',
  'America/Santiago': 'CL',
  'Pacific/Easter': 'CL',
  'America/Lima': 'PE',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES'
};

/**
 * Detects the visitor's country the same way Shopify Markets does
 * (country → market → currency), so the page opens on the right market
 * automatically. The visitor can still switch it with the selector.
 */
function detectMarketCode(): MarketCode {
  if (typeof navigator !== 'undefined') {
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const lang of languages) {
      const region = lang?.split('-')[1]?.toUpperCase() as MarketCode | undefined;
      if (region && region in markets) return region;
    }
  }
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && TIMEZONE_MARKET[zone]) return TIMEZONE_MARKET[zone];
  } catch {
    /* ignore */
  }
  return 'US';
}
export function MarketProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [code, setCode] = useState<MarketCode>(() => detectMarketCode());
  /** Markets already refreshed with Shopify's live localized amounts. */
  const [live, setLive] = useState<Partial<Record<MarketCode, Market>>>({});

  /**
   * SHOPIFY ALWAYS WINS: every time the active market changes we read the exact
   * localized amount Shopify serves for that country and use it verbatim for
   * every price on the page. The USD market is read too, so the estimated
   * reference values follow Shopify's own market ratio instead of an
   * independent exchange rate. If Shopify is unreachable, the last known
   * Shopify values stay on screen — nothing is ever converted here.
   */
  useEffect(() => {
    const controller = new AbortController();
    const needed: MarketCode[] = code === 'US' ? ['US'] : [code, 'US'];
    Promise.all(needed.map(async target => {
      const result = await fetchShopifyPrice(target, controller.signal);
      return {
        target,
        result
      };
    })).then(entries => {
      const updates: Partial<Record<MarketCode, Market>> = {};
      for (const {
        target,
        result
      } of entries) {
        if (!result) continue;
        const base = markets[target];
        const currency = result.currency || base.currency;
        const compareAtAmount = result.compareAt ?? base.compareAtAmount;
        const next: Market = {
          ...base,
          currency,
          priceAmount: result.price,
          compareAtAmount,
          live: true
        };
        next.price = formatLocal(result.price, next);
        next.compareAt = formatLocal(compareAtAmount, next);
        next.savings = formatLocal(Math.max(compareAtAmount - result.price, 0), next);
        updates[target] = next;
      }
      if (Object.keys(updates).length > 0) {
        setLive(prev => ({
          ...prev,
          ...updates
        }));
      }
    });
    return () => controller.abort();
  }, [code]);
  const value = useMemo<MarketContextValue>(() => {
    const base = live[code] ?? markets[code];
    const usdReference = live.US?.priceAmount ?? markets.US.priceAmount;
    return {
      market: {
        ...base,
        usdReference
      },
      markets: marketList.map(m => live[m.code] ?? m),
      setMarketCode: setCode,
      cartHref: cartUrl(code)
    };
  }, [code, live]);
  return <MarketContext.Provider value={value} data-id="element-372" data-mp="jbxnfn76ajm2">{children}</MarketContext.Provider>;
}
export function useMarket(): MarketContextValue {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket must be used inside MarketProvider');
  return ctx;
}